import type { ReactNode } from 'react';
import {
  action,
  hasPermission,
  resource,
  useAuthData,
} from '@auth/features/auth';
import { useEffect, useMemo, useState } from 'react';
import { trpc } from '@/shared/api/trpc';
import { toAttachmentRows } from '@/shared/lib';
import {
  AttachmentsPanel,
  Button,
  ImageLightbox,
  Modal,
  PrivateNotesPanel,
  resolveSupplierRefs,
  SupplierContactsList,
  Tab,
  TabBar,
  Text,
  toastStore,
} from '@/shared/ui';
import { toProductPreviewContent } from '../../model/product-preview-content';
import css from './product-detail-modal.module.scss';
import {
  ProductPreviewHeader,
  ProductPreviewSections,
} from './product-preview';
import {
  SupplierDocumentsSection,
  SupplierNotesSection,
} from './supplier-crm-sections';

type Props = {
  // `null` keeps the modal closed; setting an id opens it and fires the
  // byId query (gated by `enabled`).
  productId: string | null;
  onClose: () => void;
  // Optional host-itinerary language. When the modal is opened from the
  // planner products pane, the title/description/facilities render in the
  // proposal's language (legacy parity). Omitted on the standalone
  // /products page, where it defaults to 'en'.
  languageCode?: string | null;
  // Contextual primary action rendered in the footer beside Close. Supplied by
  // the host rather than built here: "Add to itinerary" only means something
  // inside the planner, and this entity must not import the itineraries
  // domain to express it.
  primaryAction?: ReactNode;
};

type TabKey = 'preview' | 'notes' | 'attachments';

const TABS: Array<{ key: TabKey; label: string }> = [
  { key: 'preview', label: 'Preview' },
  { key: 'notes', label: 'Private Notes' },
  { key: 'attachments', label: 'Attachments' },
];

// Read-only product preview modal — exact port of the legacy
// `PreviewModal` (gallery-first layout, star + budget ratings beside the
// title, facilities grid, suppliers with click-to-copy contacts, "Created
// By", and a full-screen image lightbox).
export const ProductDetailModal = ({
  productId,
  onClose,
  languageCode,
  primaryAction,
}: Props) => {
  const open = productId !== null;
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null);
  const [tab, setTab] = useState<TabKey>('preview');

  // Every opening starts on Preview — the tab is local to the window, so
  // switching to Attachments on one product must not carry into the next.
  useEffect(() => {
    if (productId) setTab('preview');
  }, [productId]);

  const { data, isLoading, isError } = trpc.products.byId.useQuery(
    { id: productId ?? '' },
    { enabled: open }
  );

  const content = useMemo(
    () => toProductPreviewContent(data, languageCode),
    [data, languageCode]
  );
  const title = content.title;
  const images = content.images;
  // Documents only — the photos are already the gallery on the Preview tab.
  const attachmentRows = useMemo(
    () => toAttachmentRows(data?.attachments),
    [data]
  );

  // Melhoria 7 §6: the action is shown only to users permitted to view CRM
  // records. Hiding it is presentation only — the CRM route runs its own
  // server-side check when it loads, so this is not the security boundary.
  const { data: authData } = useAuthData();
  const canOpenCrm = hasPermission(
    authData?.user?.permissions ?? [],
    resource.crm,
    action.read
  );

  const suppliers = useMemo(() => resolveSupplierRefs(data?.suppliers), [data]);
  const supplierIds = useMemo(
    () => [...new Set(suppliers.map((s) => s.id))].sort(),
    [suppliers]
  );

  // Supplier contacts (email/phone) — legacy enriched via /suppliers/by-ids;
  // V3 suppliers are CRM companies, resolved through companies.resolveByIds.
  const { data: supplierContacts } = trpc.companies.resolveByIds.useQuery(
    { ids: supplierIds },
    { enabled: open && supplierIds.length > 0, staleTime: 5 * 60_000 }
  );
  // `companyStatus` is kept (it used to be discarded) because Melhoria 7 §7 needs
  // to tell three cases apart: linked and resolvable, linked but archived, and
  // referenced-but-missing. A row ABSENT from this result is the missing case —
  // `resolveByIds` filters out soft-deleted and out-of-org accounts, so "not
  // returned" already means "not reachable by this user".
  const contactById = useMemo(() => {
    const map = new Map<
      string,
      {
        email: string | null;
        phone: string | null;
        companyStatus: string | null;
      }
    >();
    for (const row of supplierContacts ?? []) {
      map.set(row.id, {
        companyStatus: row.companyStatus ?? null,
        email: row.email ?? null,
        phone: row.phone ?? null,
      });
    }
    return map;
  }, [supplierContacts]);

  // Creator name — accounts.byId is admin-only; fall back to "Unknown creator"
  // (legacy parity) when unavailable so the modal never breaks for non-admins.
  const accountId = data?.accountId ?? null;
  const { data: account } = trpc.accounts.byId.useQuery(
    { id: accountId ?? '' },
    { enabled: open && !!accountId, retry: false }
  );
  const createdBy =
    [account?.firstName, account?.lastName].filter(Boolean).join(' ') ||
    'Unknown creator';

  return (
    <>
      <Modal className={css.modal} onClose={onClose} open={open}>
        <ProductPreviewHeader
          budgetTier={content.budgetTier}
          onClose={onClose}
          stars={content.stars}
          title={isLoading ? 'Product' : title}
        />

        <TabBar className={css.tabs}>
          {TABS.map((entry) => (
            <Tab
              active={tab === entry.key}
              key={entry.key}
              onClick={() => setTab(entry.key)}
            >
              {entry.label}
            </Tab>
          ))}
        </TabBar>

        <div className={css.body}>
          {isError && (
            <Text variant="error">
              Failed to load product details. It may have been deleted.
            </Text>
          )}

          {/* T-196: two clearly labelled sections, Supplier CRM first — the
              supplier record carries the commercial context (contract year,
              negotiated terms) that frames the product's own notes below it.
              The supplier half is read live from CRM and never copied onto the
              product, so one edit there updates every linked product. */}
          {tab === 'notes' && (
            <div className={css.noteSources}>
              <SupplierNotesSection
                canOpenCrm={canOpenCrm}
                source={{ productId: productId ?? '' }}
              />
              <section aria-labelledby="product-notes-heading">
                <div className={css.sourceHead}>
                  <span className={css.sourceBadge}>This product</span>
                  <div>
                    <h4 className={css.sourceTitle} id="product-notes-heading">
                      Product private notes
                    </h4>
                    <Text as="p" size="xs" variant="muted">
                      Internal notes stored only on this product.
                    </Text>
                  </div>
                </div>
                <PrivateNotesPanel
                  emptyMessage="No private notes have been added to this product."
                  notes={data?.privateNotes}
                />
              </section>
            </div>
          )}

          {/* Melhoria 6 §2: two clearly labelled sections, Supplier CRM first —
              the CRM record is the central source for contracts, tariffs and
              policies, while product documents are complementary material.
              The order is fixed and does not depend on dates or counts (§12). */}
          {tab === 'attachments' && (
            <div className={css.documentSources}>
              <SupplierDocumentsSection
                source={{ productId: productId ?? '' }}
              />
              <section aria-labelledby="product-documents-heading">
                <div className={css.sourceHead}>
                  <span className={css.sourceBadge}>This product</span>
                  <div>
                    <h4
                      className={css.sourceTitle}
                      id="product-documents-heading"
                    >
                      Product documents
                    </h4>
                    <Text as="p" size="xs" variant="muted">
                      Complementary files stored only on this product.
                    </Text>
                  </div>
                </div>
                <AttachmentsPanel
                  attachments={attachmentRows}
                  emptyMessage="No complementary documents have been added to this product."
                />
              </section>
            </div>
          )}

          {tab === 'preview' && (
            <>
              <ProductPreviewSections
                content={content}
                onDescriptionCopied={() =>
                  toastStore.getState().show({ message: 'Description copied' })
                }
                onOpenImage={setLightboxIndex}
              />

              {/* Suppliers + Created By */}
              <div className={css.twoCol}>
                <section>
                  <h4 className={css.sectionTitle}>Suppliers</h4>
                  <SupplierContactsList
                    canOpenCrm={canOpenCrm}
                    contactById={
                      supplierContacts === undefined ? undefined : contactById
                    }
                    suppliers={suppliers}
                  />
                </section>
                <section>
                  <h4 className={css.sectionTitle}>Created By</h4>
                  <Text variant="muted">{isLoading ? '—' : createdBy}</Text>
                </section>
              </div>
            </>
          )}
        </div>

        {/* Footer stays visible on every tab — the contextual action must not
            disappear because the consultant is reading notes or files. */}
        <div className={css.footer}>
          <Button onClick={onClose} variant="secondary">
            Close
          </Button>
          {primaryAction}
        </div>
      </Modal>

      {/* Full-screen image lightbox — shared viewer, portaled to <body> so it
          layers above the Modal overlay this opens from. */}
      <ImageLightbox
        images={images}
        onClose={() => setLightboxIndex(null)}
        startIndex={lightboxIndex}
        title={title}
      />
    </>
  );
};
